/**
 * Composable for searching within PDF documents
 */

import { ref, computed, type Ref, type ComputedRef, type ShallowRef } from 'vue'
import type { PDFDocumentProxy } from 'pdfjs-dist'
import type { TextContent, TextItem } from 'pdfjs-dist/types/src/display/api'
import type {
  PDFSearchResult,
  PDFSearchQuerySource,
  PDFSearchMode,
  PDFSearchSuggestion,
} from '../types'

// Unicode normalization cache for accented text search (e.g. "éducation" matches "education")
const normalizeCache = new Map<string, string>()

function normalizeText(text: string): string {
  let result = normalizeCache.get(text)
  if (result === undefined) {
    result = text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    if (normalizeCache.size > 10000) {
      const firstKey = normalizeCache.keys().next().value
      if (firstKey) normalizeCache.delete(firstKey)
    }
    normalizeCache.set(text, result)
  }
  return result
}

/**
 * Builds a mapping from normalized string indices back to original string indices.
 * NFD normalization can change string length (e.g. "é" → "e\u0301" → "e"),
 * so we need this mapping for correct highlight positioning.
 */
function buildNormalizedMapping(text: string): { normalized: string; toOriginal: number[] } {
  const nfd = text.normalize('NFD')
  const toOriginal: number[] = []
  let result = ''

  // Map NFD indices back to original indices
  const origChars = [...text]
  const nfdStr = nfd

  // Build mapping from NFD index to original char index
  const nfdToOrig: number[] = new Array(nfdStr.length)
  let nfdPos = 0
  for (let i = 0; i < origChars.length; i++) {
    const origChar = origChars[i]!
    const nfdOfChar = origChar.normalize('NFD')
    for (let j = 0; j < nfdOfChar.length; j++) {
      if (nfdPos < nfdStr.length) {
        nfdToOrig[nfdPos] = i
        nfdPos++
      }
    }
  }

  // Now strip combining diacritics and build the final mapping
  for (let i = 0; i < nfdStr.length; i++) {
    const code = nfdStr.charCodeAt(i)
    // Skip combining diacritical marks (U+0300 to U+036F)
    if (code >= 0x0300 && code <= 0x036f) continue
    result += nfdStr[i]!.toLowerCase()
    toOriginal.push(nfdToOrig[i] ?? i)
  }

  return { normalized: result, toOriginal }
}

function isWordChar(ch: string): boolean {
  return /[\p{L}'\u2019\-]/u.test(ch)
}

function extractContainingWord(fullText: string, start: number, end: number): string {
  let s = start
  while (s > 0 && isWordChar(fullText[s - 1]!)) s--
  let e = end
  while (e < fullText.length && isWordChar(fullText[e]!)) e++
  return fullText.substring(s, e).toLowerCase()
}

export interface UsePDFSearchOptions {
  document: ShallowRef<PDFDocumentProxy | null>
  getTextContent?: (pageNumber: number) => Promise<TextContent>
  onSearchStart?: (query: string) => void
  onSearchResults?: (results: PDFSearchResult[]) => void
  onResultSelect?: (result: PDFSearchResult, index: number) => void
}

export interface UsePDFSearchReturn {
  searchQuery: Ref<string>
  searchResults: Ref<PDFSearchResult[]>
  currentIndex: Ref<number>
  isSearching: Ref<boolean>
  querySource: Ref<PDFSearchQuerySource>
  searchMode: Ref<PDFSearchMode>
  suggestions: ComputedRef<PDFSearchSuggestion[]>
  navigableResults: ComputedRef<PDFSearchResult[]>
  activeHighlightMatchIndex: ComputedRef<number>
  search: (query: string) => Promise<void>
  clearSearch: () => void
  nextResult: () => void
  prevResult: () => void
  goToResult: (index: number) => void
  selectViewAll: () => void
  selectWord: (word: string) => void
}

/**
 * Composable for PDF text search with suggestion-based word grouping
 */
export function usePDFSearch(options: UsePDFSearchOptions): UsePDFSearchReturn {
  const { document, getTextContent: getTextContentFn, onSearchStart, onSearchResults, onResultSelect } = options

  // State
  const searchQuery = ref('')
  const searchResults = ref<PDFSearchResult[]>([])
  const currentIndex = ref(-1)
  const isSearching = ref(false)
  const querySource = ref<PDFSearchQuerySource>('typed')
  const searchMode = ref<PDFSearchMode>('none')
  const pendingWordSelection = ref<string | null>(null)

  // Computed: suggestions (unique words with counts)
  const suggestions = computed<PDFSearchSuggestion[]>(() => {
    if (searchResults.value.length === 0) return []

    const wordCounts = new Map<string, number>()
    for (const result of searchResults.value) {
      const w = result.containingWord
      wordCounts.set(w, (wordCounts.get(w) ?? 0) + 1)
    }

    // Filter out word === searchQuery when querySource is 'selected'
    const query = searchQuery.value.toLowerCase()
    const entries = Array.from(wordCounts.entries())
      .filter(([word]) => querySource.value !== 'selected' || word !== query)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([word, count]) => ({ word, count, isViewAll: false }))

    // Prepend "View all" with total count
    entries.unshift({ word: '', count: searchResults.value.length, isViewAll: true })

    return entries
  })

  // Computed: navigable results based on mode
  const navigableResults = computed<PDFSearchResult[]>(() => {
    if (searchMode.value === 'none') return []
    if (searchMode.value === 'all') return searchResults.value
    // 'word' mode: filter by containingWord === searchQuery
    const query = searchQuery.value.toLowerCase()
    return searchResults.value.filter((r) => r.containingWord === query)
  })

  // Computed: the matchIndex of the active navigable result (for PDFCanvas orange highlight)
  const activeHighlightMatchIndex = computed<number>(() => {
    if (currentIndex.value < 0 || currentIndex.value >= navigableResults.value.length) return -1
    return navigableResults.value[currentIndex.value]?.matchIndex ?? -1
  })

  /**
   * Searches for text in the document
   */
  async function search(query: string): Promise<void> {
    searchQuery.value = query

    if (!query.trim() || !document.value) {
      clearSearch()
      return
    }

    onSearchStart?.(query)
    isSearching.value = true
    searchResults.value = []
    currentIndex.value = -1

    try {
      const pdfDoc = document.value
      const results: PDFSearchResult[] = []
      const normalizedQuery = normalizeText(query)

      // Search each page
      for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
        let textContent: TextContent
        if (getTextContentFn) {
          textContent = await getTextContentFn(pageNum)
        } else {
          const page = await pdfDoc.getPage(pageNum)
          textContent = await page.getTextContent()
        }

        // Build full text and track item positions
        let fullText = ''
        const textItems: Array<{ text: string; start: number; item: TextItem }> = []

        for (const item of textContent.items) {
          if ('str' in item) {
            textItems.push({
              text: item.str,
              start: fullText.length,
              item: item as TextItem,
            })
            fullText += item.str
          }
        }

        // Build normalized text with position mapping back to original
        const { normalized: normalizedText, toOriginal } = buildNormalizedMapping(fullText)
        let searchPos = 0

        while (searchPos < normalizedText.length) {
          const matchIndex = normalizedText.indexOf(normalizedQuery, searchPos)
          if (matchIndex === -1) break

          // Map back to original string position
          const origMatchStart = toOriginal[matchIndex] ?? matchIndex
          const origMatchEnd = toOriginal[matchIndex + normalizedQuery.length - 1] ?? (origMatchStart + query.length - 1)
          const origMatchLen = origMatchEnd - origMatchStart + 1

          // Find the text item containing this match
          const matchingItem = textItems.find(
            (ti) =>
              ti.start <= origMatchStart &&
              ti.start + ti.text.length > origMatchStart,
          )

          if (matchingItem && 'transform' in matchingItem.item) {
            const textItem = matchingItem.item
            const transform = textItem.transform as number[]
            const baseX = transform[4]!
            const baseY = transform[5]!
            const totalLen = matchingItem.text.length
            const fontScaleX = Math.sqrt(
              transform[0]! * transform[0]! + transform[1]! * transform[1]!,
            )
            const totalWidth =
              textItem.width || totalLen * fontScaleX * 0.6
            const height = textItem.height || fontScaleX

            // Calculate character offset within text item using original positions
            const charPos = origMatchStart - matchingItem.start
            const offsetX =
              totalLen > 0 ? (charPos / totalLen) * totalWidth : 0
            const matchWidth =
              totalLen > 0 ? (origMatchLen / totalLen) * totalWidth : totalWidth

            const containingWord = extractContainingWord(fullText, origMatchStart, origMatchStart + origMatchLen)

            results.push({
              pageNumber: pageNum,
              text: fullText.substring(origMatchStart, origMatchStart + origMatchLen),
              matchIndex: results.length,
              containingWord,
              transform: [
                transform[0]!,
                transform[1]!,
                transform[2]!,
                transform[3]!,
                baseX + offsetX,
                baseY,
              ],
              width: matchWidth,
              height,
            })
          }

          searchPos = matchIndex + 1
        }
      }

      searchResults.value = results

      // After search: handle pending word selection or reset to suggestion mode
      if (pendingWordSelection.value !== null) {
        searchMode.value = 'word'
        pendingWordSelection.value = null
        if (navigableResults.value.length > 0) {
          goToResult(0)
        }
      } else {
        searchMode.value = 'none'
        querySource.value = 'typed'
      }

      onSearchResults?.(results)
    } catch (error) {
      console.error('Search error:', error)
      searchResults.value = []
    } finally {
      isSearching.value = false
    }
  }

  /**
   * Clears search state
   */
  function clearSearch(): void {
    searchQuery.value = ''
    searchResults.value = []
    currentIndex.value = -1
    isSearching.value = false
    searchMode.value = 'none'
    querySource.value = 'typed'
    pendingWordSelection.value = null
  }

  /**
   * Goes to next navigable result
   */
  function nextResult(): void {
    if (navigableResults.value.length === 0) return
    const newIndex = (currentIndex.value + 1) % navigableResults.value.length
    goToResult(newIndex)
  }

  /**
   * Goes to previous navigable result
   */
  function prevResult(): void {
    if (navigableResults.value.length === 0) return
    const newIndex =
      currentIndex.value <= 0
        ? navigableResults.value.length - 1
        : currentIndex.value - 1
    goToResult(newIndex)
  }

  /**
   * Goes to a specific navigable result by index
   */
  function goToResult(index: number): void {
    if (index < 0 || index >= navigableResults.value.length) return

    currentIndex.value = index
    const result = navigableResults.value[index]
    if (result) onResultSelect?.(result, index)
  }

  /**
   * Selects "View all" mode: navigate all substring matches
   */
  function selectViewAll(): void {
    searchMode.value = 'all'
    if (navigableResults.value.length > 0) {
      goToResult(0)
    }
  }

  /**
   * Selects a specific word: updates search query and enters word mode
   */
  function selectWord(word: string): void {
    pendingWordSelection.value = word
    querySource.value = 'selected'
    searchQuery.value = word
    search(word)
  }

  return {
    searchQuery,
    searchResults,
    currentIndex,
    isSearching,
    querySource,
    searchMode,
    suggestions,
    navigableResults,
    activeHighlightMatchIndex,
    search,
    clearSearch,
    nextResult,
    prevResult,
    goToResult,
    selectViewAll,
    selectWord,
  }
}
